import { store } from '@wordpress/interactivity';
import { formatPrice, formatPriceSmart, convertStoreApiPrice } from './formatters.js';
import { decodeHTMLEntities, getThumbnailImageUrl } from './dom-utils.js';

function formatVariationValue(rawValue) {
	const decoded = decodeHTMLEntities(rawValue || '').trim();
	if (!decoded) {
		return '';
	}

	// Humanize taxonomy-like slugs: "light-blue" -> "Light Blue"
	if (/^[a-z0-9]+(?:[-_][a-z0-9]+)+$/.test(decoded)) {
		return decoded
			.replace(/[-_]+/g, ' ')
			.replace(/\b[a-z]/g, (char) => char.toUpperCase());
	}

	return decoded;
}

/**
 * Convert Store API cart item to Caddy format with proper price calculations
 *
 * @param {Object} storeApiItem Cart item from WooCommerce Store API
 * @returns {Object} Cart item in Caddy format
 */
export function convertStoreApiItemToCaddyFormat(storeApiItem) {
	const regularPrice = convertStoreApiPrice(storeApiItem.prices?.regular_price);
	const salePrice = convertStoreApiPrice(storeApiItem.prices?.sale_price);

	// Store API totals are always ex-tax; include tax when cart display is tax-inclusive
	let lineTotal = convertStoreApiPrice(storeApiItem.totals?.line_total);
	try {
		const { state: cartState } = store('caddy/cart');
		if (cartState?.taxDisplayCart === 'incl') {
			lineTotal += convertStoreApiPrice(storeApiItem.totals?.line_total_tax || '0');
		}
	} catch (e) {
		// Store not yet initialized
	}

	// Determine if item is on sale: sale price must be less than regular price
	// Store API always returns a sale_price, even when not on sale (it equals regular_price)
	const isOnSale = salePrice < regularPrice;

	// Current price is sale price if on sale, otherwise regular price (per unit)
	// Use actual line total / quantity when it differs from product price (add-ons, custom pricing)
	let currentUnitPrice = isOnSale ? salePrice : regularPrice;
	if (lineTotal > 0 && storeApiItem.quantity > 0) {
		const actualUnitPrice = lineTotal / storeApiItem.quantity;
		// If actual unit price differs from product price, use actual (includes add-on costs)
		if (Math.abs(actualUnitPrice - currentUnitPrice) > 0.01) {
			currentUnitPrice = actualUnitPrice;
		}
	}

	// Calculate line totals (unit price × quantity) for display
	let currentLineTotal = currentUnitPrice * storeApiItem.quantity;
	let regularLineTotal = regularPrice * storeApiItem.quantity;
	const saleLineTotal = salePrice * storeApiItem.quantity;

	// Calculate savings percentage based on unit prices
	let savingsPercentage = 0;
	if (isOnSale && regularPrice > 0) {
		savingsPercentage = Math.round(((regularPrice - salePrice) / regularPrice) * 100);
	}

	// Decode HTML entities in product name to prevent double encoding
	let decodedName = decodeHTMLEntities(storeApiItem.name);

	// For variations, append attribute values to the name (matches PHP's $product->get_name())
	if (storeApiItem.variation && Array.isArray(storeApiItem.variation) && storeApiItem.variation.length > 0) {
		const variationValues = storeApiItem.variation
			.map((attr) => formatVariationValue(attr.value))
			.filter(Boolean);
		if (variationValues.length > 0) {
			decodedName = decodedName + ' - ' + variationValues.join(', ');
		}
	}

	// Extract add-on / custom field data as variationText (shown below product name)
	let variationText = '';
	if (storeApiItem.item_data && Array.isArray(storeApiItem.item_data) && storeApiItem.item_data.length > 0) {
		variationText = storeApiItem.item_data
			.map((attr) => {
				const stripAndDecode = (s) => {
					const txt = document.createElement('textarea');
					txt.innerHTML = (s || '').replace(/<[^>]*>/g, '');
					return txt.value.trim();
				};
				const rawValue = stripAndDecode(attr.display || attr.value);
				const name = stripAndDecode(attr.name || attr.key);
				if (!rawValue) return '';
				return name ? `${name}: ${rawValue}` : rawValue;
			})
			.filter(Boolean)
			.join('\n');
	}

	// Check bundle status using WooCommerce Product Bundles extension data
	const isBundleContainer = storeApiItem.type === 'bundle';
	const isBundledItem = !!(storeApiItem.extensions?.bundles?.bundled_by);
	const bundledBy = storeApiItem.extensions?.bundles?.bundled_by || null;

	// Build item class string
	let itemClass = 'cc-cart-product-list cc-cart-item';
	if (isBundleContainer) {
		itemClass += ' bundle';
	}
	if (isBundledItem) {
		itemClass += ' bundled_child';
	}

	// Extract quantity limits from Store API (handles sold_individually, min/max qty)
	const quantityLimits = storeApiItem.quantity_limits || {};
	const maxQuantity = quantityLimits.maximum || Infinity;
	const minQuantity = quantityLimits.minimum || 1;
	const soldIndividually = maxQuantity === 1;

	// Bundled items with variable qty (min != max) allow quantity changes
	const bundledHasVariableQty = isBundledItem && minQuantity < maxQuantity;

	// Compute display flags for bundle items
	const shouldHideControls = isBundledItem;
	const hideQuantity = false;
	const hideQuantityButtons = isBundledItem && !bundledHasVariableQty;
	const hidePrice = !isBundleContainer && currentLineTotal === 0 && regularLineTotal === 0;

	// Add CSS class for fixed-qty bundled items (CSS hides +/- buttons)
	if (isBundledItem && !bundledHasVariableQty) {
		itemClass += ' bundled_fixed_qty';
	}

	return {
		cartKey: storeApiItem.key,
		productId: storeApiItem.id,
		quantity: storeApiItem.quantity,
		name: decodedName,
		variationText: variationText,
		price: formatPriceSmart(currentLineTotal),
		priceHtml: formatPrice(currentLineTotal),
		regularPrice: regularPrice,
		regularLineTotal: regularLineTotal,
		regularPriceFormatted: formatPriceSmart(regularLineTotal),
		regularPriceHtml: isOnSale ? formatPrice(regularLineTotal) : '',
		salePrice: formatPriceSmart(saleLineTotal),
		unitPrice: currentUnitPrice,
		hasCustomPricing: !!(storeApiItem.item_data && storeApiItem.item_data.length > 0),
		isOnSale: isOnSale,
		savingsPercentage: savingsPercentage,
		lineTotal: lineTotal,
		lineTotalFormatted: storeApiItem.totals?.line_total_formatted || formatPrice(lineTotal),
		image: getThumbnailImageUrl(storeApiItem.images?.[0]?.thumbnail || storeApiItem.images?.[0]?.src),
		permalink: storeApiItem.permalink || `${window.location.origin}/?p=${storeApiItem.id}`,
		isBundleContainer: isBundleContainer,
		isBundledItem: isBundledItem,
		bundledBy: bundledBy,
		shouldHideControls: shouldHideControls,
		hideQuantity: hideQuantity,
		hideQuantityButtons: hideQuantityButtons,
		hidePrice: hidePrice,
		itemClass: itemClass,
		showSalePrice: isOnSale,
		showSavings: isOnSale && savingsPercentage > 0,
		soldIndividually: soldIndividually,
		maxQuantity: maxQuantity,
		minQuantity: minQuantity,
		isAtMinQty: storeApiItem.quantity <= minQuantity,
		isAtMaxQty: storeApiItem.quantity >= maxQuantity,
	};
}

/**
 * Convert all Store API cart items and aggregate bundle container prices from children
 */
export function convertCartItems(storeApiItems) {
	const items = storeApiItems.map(item => convertStoreApiItemToCaddyFormat(item));
	// For per-item-priced bundle containers showing $0, sum children's prices
	for (const item of items) {
		if (item.isBundleContainer && parseFloat(item.price) === 0) {
			const children = items.filter(child => child.bundledBy === item.cartKey);
			if (children.length > 0) {
				const aggTotal = children.reduce((sum, child) => sum + (parseFloat(child.price) || 0), 0);
				if (aggTotal > 0) {
					item.price = formatPriceSmart(aggTotal);
					item.priceHtml = formatPrice(aggTotal);
					item.regularLineTotal = aggTotal;
					item.regularPriceFormatted = formatPriceSmart(aggTotal);
					item.lineTotal = aggTotal;
					item.lineTotalFormatted = formatPrice(aggTotal);
				}
			}
		}
	}
	return items;
}
